import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { submitContactForm } from '../../../redux/contactUs/contactUsActions';
import { fetchUserData, clearUserData } from '../../../redux/agent/agentActions';
import {
  sendAgentMessage,
  synthesizeAgentSpeech,
} from '../utils/agentApiUtils';
import { replyTextForMessage } from '../utils/agentReplyUtils';
import { REALTIME_VOICE } from '../utils/agentApiUtils';
import { resolveSpaceImagePublicId } from '../utils/cloudinaryImageUtils';
import { resolveSpaceCardBookingUrl, toRelativeBookingPath } from '../utils/bookingUrlUtils';
import { saveChatSession, loadChatSession, clearChatSession } from '../utils/sessionPersistence';
import { buildChipRemovalMessage, formatRadiusFilterLabel } from '../utils/chipRemovalMessages';
import { getEmptySearchAreaSuggestion } from '../utils/emptySearchUtils';
import { shouldHideChatbot, getCountryNameFromCode } from '../utils/urlUtils';
import { useActiveCountry } from '../../../hooks/useCountryPath';
import { IMAGES_BASE_URL, CALENDER_DISPLAY_DATE_FORMAT } from '../../../settings';
import { buildAgentSearchResultsUrl } from '../../../helper/Result';
import moment from 'moment';
import {
  getInitialContactFormState,
  validateContactForm,
  sanitizeFormData,
  formatFormDataForSubmission,
  resetForm,
  isFormReadyToSubmit,
} from '../utils/formUtils';
import { handleFormFieldChange } from '../utils/formUtils';
import {
  CHATBOT_CONFIG,
  CONTACT_FORM_MESSAGES,
  DISCLAIMER_MESSAGES,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  FORM_BUTTONS,
  FORM_FIELD_NAMES,
  INPUT_TYPES,
  INFO_PANEL_MESSAGES,
  CHAT_COMMANDS,
  HELP_INFORMATION,
  LOADING_MESSAGES,
} from '../constants';
import '../../../assets/scss/components/Agent/ChatbotPopup.scss';
import VoiceModeBar from '../voice/VoiceModeBar';
import VoiceModePromo from '../voice/VoiceModePromo';
import '../../../assets/scss/components/Agent/VoiceMode.scss';
import RealtimeVoiceClient from '../voice/realtimeVoiceClient';

const VOICE_MODE_V2_ENABLED = process.env.REACT_APP_VOICE_MODE_V2 === 'true';

// ─── Simple markdown renderer ─────────────────────────────────────────────────
// Handles **bold**, [label](url) links, and newlines returned by the LLM
const MD_LINK_PATTERN = /(\[[^\]]+\]\([^)]+\))/g;
const MD_LINK_PARSE = /^\[([^\]]+)\]\(([^)]+)\)$/;

const renderInlinePart = (part, key) => {
  if (!part) return null;

  const linkMatch = part.match(MD_LINK_PARSE);
  if (linkMatch) {
    const [, label, rawUrl] = linkMatch;
    const href = toRelativeBookingPath(rawUrl) || rawUrl;
    return (
      <a
        key={key}
        href={href}
        className="chatbot-inline-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }

  return part.split(/(\*\*[^*]+\*\*)/g).map((segment, j) => {
    if (segment.startsWith('**') && segment.endsWith('**')) {
      return <strong key={`${key}-b-${j}`}>{segment.slice(2, -2)}</strong>;
    }
    return segment || null;
  });
};

const renderText = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => (
    <span key={i}>
      {line.split(MD_LINK_PATTERN).map((part, j) => renderInlinePart(part, `${i}-${j}`))}
      {i < lines.length - 1 && <br />}
    </span>
  ));
};

const getSpeechRecognition = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const getSpeechSynthesis = () => {
  if (typeof window === 'undefined') return null;
  return window.speechSynthesis || null;
};

const getAudioPlaybackSupported = () => (
  typeof window !== 'undefined'
  && typeof window.Audio === 'function'
  && window.URL
  && typeof window.URL.createObjectURL === 'function'
);

const RESPONSE_VOICE_STORAGE_KEY = 'spacey.responseVoiceURI';
const OPENAI_RESPONSE_VOICES = [
  { voiceURI: 'alloy', name: 'Alloy', lang: 'en-US', description: 'Balanced English' },
  { voiceURI: 'ash', name: 'Ash', lang: 'en-US', description: 'Clear English' },
  { voiceURI: 'coral', name: 'Coral', lang: 'en-US', description: 'Warm English' },
  { voiceURI: 'echo', name: 'Echo', lang: 'en-US', description: 'Bright English' },
  { voiceURI: 'fable', name: 'Fable', lang: 'en-US', description: 'Expressive English' },
  { voiceURI: 'onyx', name: 'Onyx', lang: 'en-US', description: 'Deep English' },
  { voiceURI: 'nova', name: 'Nova', lang: 'en-US', description: 'Friendly English' },
  { voiceURI: 'sage', name: 'Sage', lang: 'en-US', description: 'Calm English' },
  { voiceURI: 'shimmer', name: 'Shimmer', lang: 'en-US', description: 'Soft English' },
];

const combineVoiceTranscript = (baseText, transcript) => {
  const cleanTranscript = (transcript || '').trim();
  if (!cleanTranscript) return baseText;
  return `${(baseText || '').trim()} ${cleanTranscript}`.trim();
};

const stopSpeechRecognition = (recognition) => {
  if (!recognition) return;
  try {
    recognition.stop();
  } catch {
    // Some browsers throw if recognition is already inactive.
  }
};

const normalizeSpeechText = (text) => (text || '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/\s+/g, ' ')
  .trim();

const getDefaultEnglishVoice = (synthesis) => {
  if (!synthesis) return null;
  const voices = synthesis.getVoices ? synthesis.getVoices() : [];
  const englishVoices = voices.filter((voice) => /^en[-_]/i.test(voice.lang));
  return englishVoices.find((voice) => voice.default)
    || englishVoices[0]
    || null;
};

const getSavedResponseVoiceURI = () => {
  if (typeof window === 'undefined' || !window.localStorage) return '';
  try {
    return window.localStorage.getItem(RESPONSE_VOICE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const saveResponseVoiceURI = (voiceURI) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (voiceURI) {
      window.localStorage.setItem(RESPONSE_VOICE_STORAGE_KEY, voiceURI);
    } else {
      window.localStorage.removeItem(RESPONSE_VOICE_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures so playback still works.
  }
};

const findVoiceByURI = (voices, voiceURI) => voices.find((voice) => voice.voiceURI === voiceURI) || null;

const getVoiceLabel = (voice) => {
  if (!voice) return 'Default English voice';
  return voice.description ? `${voice.name} - ${voice.description}` : `${voice.name} (${voice.lang})`;
};

// ─── Space card helpers ───────────────────────────────────────────────────────

const COUNTRY_URL_MAP = {
  LK: 'Sri-Lanka',
  SG: 'Singapore',
};

const PRIVATE_OFFICE_EVENT_TYPE = 'WORK_PRIV';
const PRIVATE_OFFICE_EVENT_TYPE_ID = '5e8e0fe22d2c6104bdf5569a';

const isPrivateOfficeEventType = (eventTypeId = '') => {
  const normalized = String(eventTypeId || '').trim();
  return normalized.toUpperCase() === PRIVATE_OFFICE_EVENT_TYPE
    || normalized === PRIVATE_OFFICE_EVENT_TYPE_ID;
};

const formatBookingDateParam = (raw) => {
  if (!raw) return raw;
  const parsed = moment(raw, ['DD/MM/YYYY', 'DD-MM-YYYY', 'D/M/YYYY', 'D-M-YYYY'], true);
  return parsed.isValid() ? parsed.format(CALENDER_DISPLAY_DATE_FORMAT) : raw;
};

const buildBookingUrl = (
  spaceId,
  countryCode,
  unit = 'hour',
  startDate,
  endDate,
  eventTypeId = '',
  startTime,
  endTime,
  peopleCount,
) => {
  const country = getCountryNameFromCode(countryCode) || COUNTRY_URL_MAP[countryCode] || 'Sri-Lanka';
  const normalizedStartDate = formatBookingDateParam(startDate);
  const normalizedEndDate = formatBookingDateParam(endDate || startDate);
  const dateParams = normalizedStartDate
    ? `&urlStartDate=${normalizedStartDate}&urlEndDate=${normalizedEndDate || normalizedStartDate}`
    : '';
  const timeParams = startTime && endTime
    ? `&urlStartTime=${encodeURIComponent(startTime)}&urlEndTime=${encodeURIComponent(endTime)}`
    : '';
  const bookingPeopleCount = isPrivateOfficeEventType(eventTypeId) ? 1 : Number(peopleCount);
  const guestParam = bookingPeopleCount && bookingPeopleCount > 0
    ? `&urlPeopleCount=${bookingPeopleCount}`
    : '';
  const eventParam = eventTypeId ? `&eventTypeId=${eventTypeId}` : '';
  return `/${country}/space/bookings?spaceId=${spaceId}${eventParam}${dateParams}${timeParams}&unit=${unit}${guestParam}`;
};

const buildImageUrl = (publicId) => {
  if (!publicId) return null;
  if (/^https?:\/\//i.test(publicId)) return publicId;
  return `${IMAGES_BASE_URL}${publicId}`;
};

const buildMoreResultsHint = (remainingCount) => (
  `+${remainingCount} more — narrow down with space type, location, capacity, `
  + 'amenities, budget, date/time, or intended activity.'
);

// ─── Component ────────────────────────────────────────────────────────────────

const ChatbotPopupV2 = ({ isOpen, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [agentHistory, setAgentHistory] = useState([]);  // LLM conversation history
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [contactForm, setContactForm] = useState(getInitialContactFormState());
  const [contactFormErrors, setContactFormErrors] = useState({});
  const [initialized, setInitialized] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState('');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [speechSupported, setSpeechSupported] = useState(() => !!getSpeechRecognition());
  const [isListening, setIsListening] = useState(false);
  const [speechError, setSpeechError] = useState('');
  const [playbackSupported] = useState(() => getAudioPlaybackSupported() || !!getSpeechSynthesis());
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [playbackLoadingMessageId, setPlaybackLoadingMessageId] = useState(null);
  const [availableEnglishVoices] = useState(OPENAI_RESPONSE_VOICES);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(() => getSavedResponseVoiceURI());
  const voiceModeSupported = VOICE_MODE_V2_ENABLED && RealtimeVoiceClient.isSupported();
  const [voiceModeActive, setVoiceModeActive] = useState(false);
  /** After Cancel from live voice, show header mic only until Reset chat. */
  const [voicePromoDismissed, setVoicePromoDismissed] = useState(false);
  const voiceToolResultReceivedRef = useRef(false);
  const pendingVoiceResultRef = useRef(null);
  const voiceSearchFallbackTimerRef = useRef(null);
  const voiceSearchInflightRef = useRef(false);
  const pendingBookingUrlRef = useRef(null);

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const voiceBaseInputRef = useRef('');
  const utteranceRef = useRef(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const playbackRequestRef = useRef(0);
  const dispatch = useDispatch();

  // ─── Offline detection ───────────────────────────────────────────────────
  useEffect(() => {
    const setOnline  = () => setIsOffline(false);
    const setOffline = () => setIsOffline(true);
    window.addEventListener('online',  setOnline);
    window.addEventListener('offline', setOffline);
    return () => {
      window.removeEventListener('online',  setOnline);
      window.removeEventListener('offline', setOffline);
    };
  }, []);

  // ─── Voice input ──────────────────────────────────────────────────────────
  useEffect(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      setSpeechSupported(false);
      return undefined;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsListening(true);
      setSpeechError('');
    };

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      setInputMessage(combineVoiceTranscript(voiceBaseInputRef.current, transcript));
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setSpeechError('Microphone access is blocked. Please allow microphone access to use voice input.');
      } else if (event.error !== 'aborted') {
        setSpeechError('Voice input stopped. Please try again.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      stopSpeechRecognition(recognition);
      recognitionRef.current = null;
    };
  }, []);

  // ─── Response playback ────────────────────────────────────────────────────
  const stopResponsePlayback = useCallback((clearState = true) => {
    playbackRequestRef.current += 1;
    const synthesis = getSpeechSynthesis();
    if (synthesis) synthesis.cancel();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (
      audioUrlRef.current
      && typeof window !== 'undefined'
      && window.URL
      && window.URL.revokeObjectURL
    ) {
      window.URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = '';
    }
    utteranceRef.current = null;
    if (clearState) setSpeakingMessageId(null);
    setPlaybackLoadingMessageId(null);
  }, []);

  useEffect(() => () => {
    stopResponsePlayback(false);
  }, [stopResponsePlayback]);

  useEffect(() => {
    setSelectedVoiceURI((currentVoiceURI) => (
      findVoiceByURI(OPENAI_RESPONSE_VOICES, currentVoiceURI) ? currentVoiceURI : ''
    ));
  }, []);

  // ─── Auth & user data ────────────────────────────────────────────────────
  const authDetails = useSelector((state) => state?.auth?.loginDetails?.data?.token || null);
  const isLoggedIn = !!authDetails;
  const wasLoggedInRef = useRef(isLoggedIn);
  const userData = useSelector((state) => state?.agent?.userData || null);
  const userDataLoading = useSelector((state) => state?.agent?.userDataLoading || false);

  const activeCountry = useActiveCountry();
  const marketCode = activeCountry?.code || 'LK';
  const marketCurrency = activeCountry?.currency || 'LKR';
  const countryWideLabel = activeCountry?.name
    ? `across ${activeCountry.name}`
    : 'across Sri Lanka';

  const chipMarketOptions = useMemo(() => ({
    currency: marketCurrency,
    countryWideLabel,
  }), [marketCurrency, countryWideLabel]);

  const emptyAreaSuggestion = useMemo(
    () => getEmptySearchAreaSuggestion(marketCode),
    [marketCode],
  );

  const userContext = useMemo(() => ({
    isLoggedIn,
    userName: userData?.userName || '',
    bookingCount: userData?.bookingCount || 0,
    countryCode: marketCode,
    currency: marketCurrency,
  }), [isLoggedIn, userData, marketCode, marketCurrency]);

  const prevMarketCodeRef = useRef(marketCode);

  useEffect(() => {
    if (prevMarketCodeRef.current === marketCode) return;
    prevMarketCodeRef.current = marketCode;
    clearChatSession();
    setAgentHistory([]);
    setShowContactForm(false);
    setInputMessage('');
    setVoiceModeActive(false);
    setVoicePromoDismissed(false);
    setMessages([{
      id: Date.now(),
      text: buildGreeting(isLoggedIn, userData, activeCountry?.name),
      sender: 'bot',
      timestamp: new Date(),
    }]);
    setInitialized(true);
  }, [marketCode, isLoggedIn, userData]);

  // ─── Fetch user data on login ────────────────────────────────────────────
  useEffect(() => {
    if (isLoggedIn && !userData && !userDataLoading) {
      dispatch(fetchUserData());
    }
    if (!isLoggedIn) {
      dispatch(clearUserData());
    }
  }, [isLoggedIn]);

  // ─── Phase 9 — restore or start chat on open ───────────────────────────────
  useEffect(() => {
    if (!isOpen || initialized) return;
    if (isLoggedIn && userDataLoading) return;

    const saved = loadChatSession(marketCode);
    if (saved) {
      setMessages(saved.messages);
      setAgentHistory(saved.agentHistory);
    } else {
      const greeting = buildGreeting(isLoggedIn, userData, activeCountry?.name);
      setMessages([{ id: 1, text: greeting, sender: 'bot', timestamp: new Date() }]);
      setAgentHistory([]);
    }
    setInitialized(true);
  }, [isOpen, isLoggedIn, userData, userDataLoading, initialized, marketCode]);

  // ─── Phase 9 — persist chat (including while popup is closed) ──────────────
  useEffect(() => {
    if (!initialized || messages.length === 0) return;
    saveChatSession({ messages, agentHistory, countryCode: marketCode });
  }, [messages, agentHistory, initialized, marketCode]);

  // ─── Close popup — keep in-memory thread; flush sessionStorage ─────────────
  useEffect(() => {
    if (!isOpen) {
      if (initialized && messages.length > 0) {
        saveChatSession({ messages, agentHistory, countryCode: marketCode });
      }
      stopSpeechRecognition(recognitionRef.current);
      stopResponsePlayback();
      setShowContactForm(false);
      setInputMessage('');
    }
  }, [isOpen, initialized, messages, agentHistory, stopResponsePlayback]);

  useEffect(() => {
    if (showContactForm && recognitionRef.current) {
      stopSpeechRecognition(recognitionRef.current);
    }
  }, [showContactForm]);

  // ─── Track auth transitions without wiping chat state ───────────────────────
  useEffect(() => {
    // Keep the current conversation when auth changes.
    // Greeting text is refreshed by the dedicated greeting sync effect.
    wasLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  // Refresh the greeting message when auth state changes mid-chat.
  // This keeps the welcome text in sync without forcing a full reset.
  useEffect(() => {
    if (!initialized || messages.length === 0) return;
    if (isLoggedIn && userDataLoading) return;

    const nextGreeting = buildGreeting(isLoggedIn, userData, activeCountry?.name);
    setMessages((prev) => {
      if (!prev.length) return prev;
      const first = prev[0];
      if (first.sender !== 'bot') return prev;
      if ((first.text || '') === nextGreeting) return prev;
      return [{ ...first, text: nextGreeting }, ...prev.slice(1)];
    });
  }, [initialized, isLoggedIn, userData, userDataLoading]);

  // ─── Auto-scroll ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ─── URL guard ───────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => { if (isOpen && shouldHideChatbot()) onClose(); };
    check();
    window.addEventListener('popstate', check);
    return () => window.removeEventListener('popstate', check);
  }, [isOpen, onClose]);

  // Reset close animation when reopening (otherwise popup stays invisible)
  useEffect(() => {
    if (isOpen) setIsClosing(false);
  }, [isOpen]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleClose = () => {
    stopSpeechRecognition(recognitionRef.current);
    stopResponsePlayback();
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  const handleReload = () => {
    stopResponsePlayback();
    clearChatSession();
    setMessages([{ id: 1, text: buildGreeting(isLoggedIn, userData, activeCountry?.name), sender: 'bot', timestamp: new Date() }]);
    setAgentHistory([]);
    setShowContactForm(false);
    setInputMessage('');
    resetForm(setContactForm);
    setContactFormErrors({});
    setVoiceModeActive(false);
    setVoicePromoDismissed(false);
  };

  /**
   * dispatchMessage — send a message programmatically (chip removal, retry).
   * Bypasses the input field entirely.
   */
  const dispatchMessage = useCallback(async (text) => {
    if (!text || loading) return;

    if (isOffline) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: 'You appear to be offline. Reconnect and try again.', sender: 'bot', timestamp: new Date() },
      ]);
      return;
    }

    const userMsg    = { id: Date.now(),     text, sender: 'user', timestamp: new Date() };
    const loadingMsg = { id: Date.now() + 1, text: 'Thinking...', sender: 'bot', timestamp: new Date(), isLoading: true };

    setLastUserMessage(text);
    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setLoading(true);

    try {
      const {
        reply, action, spaces, filters, searchMeta, pagination, searchContext, history,
      } = await sendAgentMessage(text, agentHistory, userContext);

      setMessages((prev) => [
        ...prev.filter((m) => !m.isLoading),
        {
          id: Date.now() + 2,
          text: reply,
          sender: 'bot',
          timestamp: new Date(),
          spaces: spaces || null,
          filters: filters || null,
          searchMeta: searchMeta || null,
          pagination: pagination || null,
          searchContext: searchContext || null,
          retryText: text,
          bookingUrl: action?.type === 'open_booking' && action?.bookingUrl
            ? (toRelativeBookingPath(action.bookingUrl) || action.bookingUrl)
            : null,
        },
      ]);
      setAgentHistory(history);

      if (action?.action === 'show_contact_form') setShowContactForm(true);
      if (action?.type === 'open_booking' && action?.bookingUrl) {
        const bookingPath = toRelativeBookingPath(action.bookingUrl) || action.bookingUrl;
        window.open(bookingPath, '_blank');
      }
    } catch {
      setMessages((prev) => [
        ...prev.filter((m) => !m.isLoading),
        {
          id: Date.now() + 2,
          text: 'I\'m having trouble connecting right now.',
          sender: 'bot',
          timestamp: new Date(),
          isError: true,
          retryText: text,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [loading, agentHistory, userContext, isOffline]);

  // Heuristic: does this utterance look like a space search or refinement request?
  const looksLikeSpaceSearch = useCallback((text) => {
    const t = (text || '').toLowerCase();
    if (!t || t.length < 3) return false;
    return /\b(coworking|co-working|meeting room|meeting space|private office|virtual office|interview room|team building|training room|photo shoot|video shoot|photoshoot|workspace|workspaces?|spaces?|office)\b/.test(t)
      || /\b(find|show|search|need|want|looking for|book|refine|narrow|filter)\b/.test(t)
      || /\b(under|below|less than|budget|rupee|rupees|lkr|cheap|affordable|maximum|max)\b/.test(t)
      || /\b(colombo|kandy|galle|jaffna|negombo|matara)\b/.test(t)
      || /\b\d{3,}\b/.test(t);
  }, []);

  const voiceCardFields = (result) => ({
    spaces: result?.spaces || null,
    filters: result?.filters || null,
    searchMeta: result?.searchMeta || null,
    pagination: result?.pagination || null,
    searchContext: result?.searchContext || null,
  });

  const appendVoiceCardMessage = useCallback((result) => {
    if (!result) return;
    const hasSpaces = Array.isArray(result.spaces) && result.spaces.length > 0;
    if (!hasSpaces && !result.searchMeta) return;

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + Math.floor(Math.random() * 1000),
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        fromVoice: true,
        ...voiceCardFields(result),
      },
    ]);
  }, []);

  // Handles a tool result from the live voice session (search / booking / contact).
  const handleVoiceToolResult = useCallback((result) => {
    if (!result) return;
    voiceToolResultReceivedRef.current = true;
    if (voiceSearchFallbackTimerRef.current) {
      clearTimeout(voiceSearchFallbackTimerRef.current);
      voiceSearchFallbackTimerRef.current = null;
    }

    const action = result.action;
    if (action?.action === 'show_contact_form') {
      setShowContactForm(true);
      return;
    }
    if (action?.type === 'open_booking' && action?.bookingUrl) {
      const bookingPath = toRelativeBookingPath(action.bookingUrl) || action.bookingUrl;
      pendingBookingUrlRef.current = bookingPath;
      window.open(bookingPath, '_blank');
      setMessages((prev) => {
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          const m = prev[i];
          if (m.sender === 'bot' && !m.isLoading && m.text?.trim()) {
            const updated = [...prev];
            updated[i] = { ...m, bookingUrl: bookingPath };
            pendingBookingUrlRef.current = null;
            return updated;
          }
        }
        return prev;
      });
      return;
    }

    const hasSpaces = Array.isArray(result.spaces) && result.spaces.length > 0;
    if (!hasSpaces && !result.searchMeta) return;

    pendingVoiceResultRef.current = result;

    // Tool finished after the spoken reply — attach cards to that bubble (same as text chat).
    setMessages((prev) => {
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        const m = prev[i];
        if (m.fromVoice && m.sender === 'bot' && m.text?.trim() && !m.spaces?.length && !m.searchMeta) {
          const updated = [...prev];
          updated[i] = { ...m, ...voiceCardFields(result) };
          pendingVoiceResultRef.current = null;
          return updated;
        }
      }
      return prev;
    });
  }, []);

  // If the Realtime model skips tool calling, fall back to the text chat search path.
  const runVoiceSearchFallback = useCallback(async (text) => {
    if (!text || voiceSearchInflightRef.current || voiceToolResultReceivedRef.current) return;
    voiceSearchInflightRef.current = true;
    try {
      const {
        action, spaces, filters, searchMeta, pagination, searchContext, history,
      } = await sendAgentMessage(text, agentHistory, userContext);

      if (history) setAgentHistory(history);

      if (action?.action === 'show_contact_form') {
        setShowContactForm(true);
        return;
      }
      if (action?.type === 'open_booking' && action?.bookingUrl) {
        const bookingPath = toRelativeBookingPath(action.bookingUrl) || action.bookingUrl;
        window.open(bookingPath, '_blank');
        return;
      }

      if (!voiceToolResultReceivedRef.current) {
        appendVoiceCardMessage({ spaces, filters, searchMeta, pagination, searchContext });
        voiceToolResultReceivedRef.current = true;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[voice] search fallback failed:', error);
    } finally {
      voiceSearchInflightRef.current = false;
    }
  }, [agentHistory, userContext, appendVoiceCardMessage]);

  // Appends finalized voice transcripts into the chat like normal messages.
  // User speech is intentionally NOT shown — the async caption transcriber
  // (gpt-4o-mini-transcribe) is often wrong with accent/short utterances, while
  // the realtime model still hears correctly. Assistant replies + cards stay visible.
  const handleVoiceCaption = useCallback(({ role, text, final }) => {
    if (!final) return;
    const clean = (text || '').trim();
    if (!clean) return;

    if (role === 'user') {
      voiceToolResultReceivedRef.current = false;
      if (looksLikeSpaceSearch(clean)) {
        if (voiceSearchFallbackTimerRef.current) {
          clearTimeout(voiceSearchFallbackTimerRef.current);
        }
        voiceSearchFallbackTimerRef.current = setTimeout(() => {
          voiceSearchFallbackTimerRef.current = null;
          runVoiceSearchFallback(clean);
        }, 3000);
      }
      return;
    }

    const pending = pendingVoiceResultRef.current;
    pendingVoiceResultRef.current = null;

    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.fromVoice && last?.sender === 'bot' && last?.text?.trim() === clean) {
        return prev;
      }
      if (
        last?.fromVoice
        && last?.sender === 'bot'
        && last?.text?.trim()
        && clean.startsWith(last.text.trim())
        && last.text.trim().length < clean.length
        && !last.spaces?.length
      ) {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...last,
          text: clean,
          bookingUrl: pendingBookingUrlRef.current || last.bookingUrl || null,
          ...voiceCardFields(pending),
        };
        pendingBookingUrlRef.current = null;
        return updated;
      }
      return [
        ...prev,
        {
          id: Date.now() + Math.floor(Math.random() * 1000),
          text: clean,
          sender: 'bot',
          timestamp: new Date(),
          fromVoice: true,
          bookingUrl: pendingBookingUrlRef.current || null,
          ...voiceCardFields(pending),
        },
      ];
    });
    pendingBookingUrlRef.current = null;
  }, [looksLikeSpaceSearch, runVoiceSearchFallback]);

  const enterVoiceMode = useCallback(() => {
    stopSpeechRecognition(recognitionRef.current);
    stopResponsePlayback();
    voiceToolResultReceivedRef.current = false;
    pendingVoiceResultRef.current = null;
    if (voiceSearchFallbackTimerRef.current) {
      clearTimeout(voiceSearchFallbackTimerRef.current);
      voiceSearchFallbackTimerRef.current = null;
    }
    setVoiceModeActive(true);
  }, [stopResponsePlayback]);

  const exitVoiceMode = useCallback(() => {
    if (voiceSearchFallbackTimerRef.current) {
      clearTimeout(voiceSearchFallbackTimerRef.current);
      voiceSearchFallbackTimerRef.current = null;
    }
    voiceToolResultReceivedRef.current = false;
    pendingVoiceResultRef.current = null;
    pendingBookingUrlRef.current = null;
    setVoiceModeActive(false);
    setVoicePromoDismissed(true);
  }, []);

  const handleSend = useCallback(async () => {
    const text = inputMessage.trim();
    if (!text || loading || isListening) return;
    stopSpeechRecognition(recognitionRef.current);
    stopResponsePlayback();
    setInputMessage('');
    setVoicePromoDismissed(true);
    await dispatchMessage(text);
  }, [inputMessage, loading, isListening, dispatchMessage, stopResponsePlayback]);

  const handleVoiceToggle = useCallback(() => {
    if (!speechSupported || loading || !recognitionRef.current) return;

    if (isListening) {
      stopSpeechRecognition(recognitionRef.current);
      return;
    }

    voiceBaseInputRef.current = inputMessage;
    setSpeechError('');
    setIsListening(true);

    try {
      recognitionRef.current.start();
    } catch {
      setIsListening(false);
      setSpeechError('Voice input is already starting. Please try again.');
    }
  }, [speechSupported, loading, isListening, inputMessage]);

  const handleResponsePlayback = useCallback(async (msg) => {
    if (
      !playbackSupported
      || !msg
      || typeof window === 'undefined'
    ) return;

    if (speakingMessageId === msg.id) {
      stopResponsePlayback();
      return;
    }

    const speechText = normalizeSpeechText(replyTextForMessage(msg));
    if (!speechText) return;

    stopResponsePlayback();
    stopSpeechRecognition(recognitionRef.current);

    const playbackRequestId = playbackRequestRef.current;
    setSpeakingMessageId(msg.id);
    setPlaybackLoadingMessageId(msg.id);

    try {
      const selectedVoice = findVoiceByURI(availableEnglishVoices, selectedVoiceURI)
        || findVoiceByURI(availableEnglishVoices, 'alloy')
        || availableEnglishVoices[0];
      const audioBlob = await synthesizeAgentSpeech(speechText, selectedVoice?.voiceURI || 'alloy');
      if (playbackRequestId !== playbackRequestRef.current) return;
      const audioUrl = window.URL.createObjectURL(audioBlob);
      const audio = new window.Audio(audioUrl);

      audioRef.current = audio;
      audioUrlRef.current = audioUrl;
      audio.onended = () => {
        if (audioRef.current === audio) stopResponsePlayback();
      };
      audio.onerror = audio.onended;
      await audio.play();
      setPlaybackLoadingMessageId(null);
      return;
    } catch {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (audioUrlRef.current && window.URL && window.URL.revokeObjectURL) {
        window.URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = '';
      }
      if (playbackRequestId !== playbackRequestRef.current) return;
      const synthesis = getSpeechSynthesis();
      if (!synthesis || typeof window.SpeechSynthesisUtterance !== 'function') {
        stopResponsePlayback();
        return;
      }
      setPlaybackLoadingMessageId(null);

      const utterance = new window.SpeechSynthesisUtterance(speechText);
      const voice = getDefaultEnglishVoice(synthesis);
      if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang;
      } else {
        utterance.lang = 'en-US';
      }
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.onend = () => {
        if (utteranceRef.current === utterance) {
          utteranceRef.current = null;
          setSpeakingMessageId(null);
        }
      };
      utterance.onerror = utterance.onend;

      utteranceRef.current = utterance;
      synthesis.speak(utterance);
    }
  }, [
    playbackSupported,
    speakingMessageId,
    stopResponsePlayback,
    availableEnglishVoices,
    selectedVoiceURI,
  ]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  const handleContactFormChange = (e) => {
    handleFormFieldChange(e, setContactForm, contactForm);
    const { name } = e.target;
    if (contactFormErrors[name]) {
      setContactFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const validation = validateContactForm(contactForm);
      if (!validation.isValid) {
        setContactFormErrors(validation.errors);
        return;
      }
      const sanitized = sanitizeFormData(contactForm);
      const formatted = formatFormDataForSubmission(sanitized);
      await dispatch(submitContactForm(formatted));
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: "Thank you! Your message has been sent. We'll get back to you soon.", sender: 'bot', timestamp: new Date() },
      ]);
      setShowContactForm(false);
      resetForm(setContactForm);
      setContactFormErrors({});
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: 'Sorry, there was an error sending your message. Please try again.', sender: 'bot', timestamp: new Date() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ts) => {
    try { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (!isOpen || shouldHideChatbot()) return null;

  const isReady = !isLoggedIn || (isLoggedIn && userData && !userDataLoading);

  const showVoicePromo = voiceModeSupported
    && !voiceModeActive
    && !voicePromoDismissed
    && !showContactForm;
  const showHeaderVoiceButton = voiceModeSupported
    && voicePromoDismissed
    && !voiceModeActive;

  return (
    <>
      {/* Loading state while user data fetches */}
      {isOpen && !isReady && (
        <div className="chatbot-overlay">
          <div className="chatbot-popup">
            <div className="chatbot-loading-container">
              <div className="chatbot-loading-content">
                <div className="chatbot-avatar">
                  <img src={require('../../../assets/images/agent/spacey-2026-3d.svg')} alt="Spacey" width="48" height="48" />
                </div>
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <span>Loading your personalized experience...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main chat popup */}
      {isReady && (
        <div className={`chatbot-overlay ${isClosing ? 'closing' : ''}`}>
          <div className={`chatbot-popup ${isClosing ? 'closing' : ''}`}>

            {/* Header */}
            <div className="chatbot-header">
              <div className="chatbot-title">
                <div className="chatbot-avatar">
                  <img src={require('../../../assets/images/agent/spacey-2026-3d.svg')} alt="Spacey" width="32" height="32" />
                </div>
                <div className="chatbot-info">
                  <h3>{CHATBOT_CONFIG.NAME}</h3>
                </div>
              </div>
              <div className="header-actions">
                {showHeaderVoiceButton && (
                  <button
                    type="button"
                    className="voice-mode-button"
                    onClick={enterVoiceMode}
                    aria-label="Start live voice mode"
                    title="Talk to Spacey"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z" fill="currentColor" />
                      <path
                        d="M5 11a7 7 0 0 0 14 0M12 18v3"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
                <button
                  type="button"
                  className="reset-chat-button"
                  onClick={handleReload}
                  aria-label="Reset chat and start a new conversation"
                  title="Clear this chat and start fresh"
                >
                  <svg className="reset-chat-button__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M4 12a8 8 0 0 1 13.66-5.66M20 7V3h-4M20 12a8 8 0 0 1-13.66 5.66M4 17v4h4"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="reset-chat-button__label">Reset chat</span>
                </button>
                <button type="button" className="close-button" onClick={handleClose} aria-label="Close chat">
                  <img src={require('../../../assets/images/agent/close-circle.svg')} alt="" width="22" height="22" />
                </button>
              </div>
            </div>

            {/* Messages + live-voice discovery CTA */}
            <div className={`chatbot-conversation${showVoicePromo ? ' chatbot-conversation--voice-promo' : ''}`}>
            <div className={`chatbot-messages${showVoicePromo ? ' chatbot-messages--voice-promo' : ''}`}>
              {messages.map((msg, index) => (
                <div key={msg.id} className={`message ${msg.sender}`}>
                  <div className={index === 0 ? 'initial-message-content' : 'message-content'}>
                    {msg.isLoading ? (
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                        <span>{msg.text}</span>
                      </div>
                    ) : (
                      <>
                        <div className="initial-message-row">
                          <div className="message-text">
                            {index === 0 ? (
                              // First message: bold first line
                              (() => {
                                const displayText = replyTextForMessage(msg);
                                const parts = displayText.split('\n\n');
                                return (
                                  <>
                                    <strong>{parts[0]}</strong>
                                    {parts.length > 1 && <><br />{renderText(parts.slice(1).join('\n\n'))}</>}
                                  </>
                                );
                              })()
                            ) : (
                              renderText(replyTextForMessage(msg))
                            )}
                          </div>
                          {msg.bookingUrl && (
                            <a
                              href={msg.bookingUrl}
                              className="chatbot-inline-link chatbot-booking-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open booking page →
                            </a>
                          )}
                          {index === 0 && (
                            <div className="initial-message-avatar">
                              <img
                                src={require('../../../assets/images/agent/spacey-2026-3d.svg')}
                                width="100" height="100"
                                alt="Spacey"
                              />
                            </div>
                          )}
                        </div>

                        {/* Phase 8 — error state with retry */}
                        {msg.isError && (
                          <div className="v2-error-state">
                            <span className="v2-error-icon">⚠️</span>
                            <span className="v2-error-text">
                              Having trouble connecting. Check your connection or{' '}
                              <button className="v2-retry-btn" onClick={() => dispatchMessage(msg.retryText)}>
                                try again
                              </button>.
                            </span>
                            <button
                              className="v2-contact-btn"
                              onClick={() => dispatchMessage('connect me to an agent')}
                            >
                              Talk to a human →
                            </button>
                          </div>
                        )}

                        {/* Phase 8 — search empty / API error (no cards) */}
                        {msg.searchMeta && (
                          <div
                            className={`v2-search-meta v2-search-meta--${msg.searchMeta.type}`}
                          >
                            <span className="v2-search-meta-icon">
                              {msg.searchMeta.type === 'error' ? '⚠️'
                                : (msg.searchMeta.type === 'time_invalid' || msg.searchMeta.type === 'date_invalid')
                                  ? '🕐'
                                  : '🔍'}
                            </span>
                            <p className="v2-search-meta-text">
                              {msg.searchMeta.message
                                || (msg.searchMeta.type === 'error'
                                  ? 'Spaces are slow to load right now.'
                                  : msg.searchMeta.type === 'time_invalid'
                                    ? "I couldn't read that time. Try something like 10:00 am – 11:00 am."
                                    : msg.searchMeta.type === 'date_invalid'
                                      ? "That date isn't in the availability window."
                                      : 'No spaces matched those filters.')}
                            </p>
                            <div className="v2-search-meta-actions">
                              {(msg.searchMeta.type === 'time_invalid' || msg.searchMeta.type === 'date_invalid')
                                && msg.retryText && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn v2-empty-action-primary"
                                  onClick={() => dispatchMessage(msg.retryText)}
                                >
                                  Try again
                                </button>
                              )}
                              {msg.searchMeta.type === 'error' && msg.retryText && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn v2-empty-action-primary"
                                  onClick={() => dispatchMessage(msg.retryText)}
                                >
                                  Try again
                                </button>
                              )}
                              {msg.searchMeta.type === 'empty' && msg.filters?.location
                                && !emptyAreaSuggestion.alreadyMatches(msg.filters.location) && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage(
                                    emptyAreaSuggestion.dispatchQuery(msg.filters.spaceType)
                                  )}
                                >
                                  {emptyAreaSuggestion.label}
                                </button>
                              )}
                              {msg.searchMeta.type === 'empty' && msg.filters?.date && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage(
                                    buildChipRemovalMessage('date', msg.filters, chipMarketOptions)
                                  )}
                                >
                                  Search without date
                                </button>
                              )}
                              {msg.searchMeta.type === 'empty' && msg.filters?.maxPrice && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage('search without a budget limit')}
                                >
                                  Remove budget cap
                                </button>
                              )}
                              {msg.searchMeta.type === 'empty'
                                && Array.isArray(msg.filters?.amenities)
                                && msg.filters.amenities.length > 0 && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage(
                                    buildChipRemovalMessage('amenities', msg.filters, chipMarketOptions),
                                  )}
                                >
                                  Remove amenity filters
                                </button>
                              )}
                              {msg.searchMeta.type === 'error' && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage('connect me to an agent')}
                                >
                                  Talk to a human →
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Phase 2 — filter chips (also when search returned no matches) */}
                        {msg.filters && ((msg.spaces && msg.spaces.length > 0) || msg.searchMeta?.type === 'empty') && msg.searchMeta?.type !== 'time_invalid' && msg.searchMeta?.type !== 'date_invalid' && (
                          <div className="v2-filter-chips">
                            {msg.filters.spaceType && (
                              <span className="v2-chip">
                                {msg.filters.spaceType}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label={`Remove ${msg.filters.spaceType} filter`}
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('spaceType', msg.filters, chipMarketOptions))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {(() => {
                              const radiusLabel = formatRadiusFilterLabel(msg.filters);
                              if (radiusLabel) {
                                return (
                                  <span className="v2-chip">
                                    📍 {radiusLabel}
                                    <button
                                      type="button"
                                      className="v2-chip-remove"
                                      aria-label="Remove radius filter"
                                      onClick={() => dispatchMessage(buildChipRemovalMessage('radius', msg.filters, chipMarketOptions))}
                                    >
                                      <span className="v2-chip-x">✕</span>
                                    </button>
                                  </span>
                                );
                              }
                              if (msg.filters.location) {
                                return (
                                  <span className="v2-chip">
                                    📍 {msg.filters.location}
                                    <button
                                      type="button"
                                      className="v2-chip-remove"
                                      aria-label={`Remove ${msg.filters.location} filter`}
                                      onClick={() => dispatchMessage(buildChipRemovalMessage('location', msg.filters, chipMarketOptions))}
                                    >
                                      <span className="v2-chip-x">✕</span>
                                    </button>
                                  </span>
                                );
                              }
                              return null;
                            })()}
                            {msg.filters.date && (
                              <span className="v2-chip">
                                📅 {msg.filters.date}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label="Remove date filter"
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('date', msg.filters, chipMarketOptions))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {msg.filters.timeRange && (
                              <span className="v2-chip">
                                🕐 {msg.filters.timeRange}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label="Remove time filter"
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('time', msg.filters, chipMarketOptions))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {msg.filters.peopleCount && (
                              <span className="v2-chip">
                                👥 {msg.filters.peopleCount}+ people
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label="Remove capacity filter"
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('peopleCount', msg.filters, chipMarketOptions))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {msg.filters.maxPrice && (
                              <span className="v2-chip">
                                💰 ≤ {marketCurrency} {msg.filters.maxPrice.toLocaleString()}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label="Remove budget filter"
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('maxPrice', msg.filters, chipMarketOptions))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {Array.isArray(msg.filters.amenities) && msg.filters.amenities.map((amenity, amenityIdx) => (
                              <span key={`amenity-${amenityIdx}-${amenity}`} className="v2-chip v2-chip-amenity">
                                {amenity}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label={`Remove ${amenity} filter`}
                                  onClick={() => dispatchMessage(
                                    buildChipRemovalMessage('amenity', msg.filters, { ...chipMarketOptions, amenityName: amenity }),
                                  )}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Phase 3 — rich space cards */}
                        {msg.spaces && msg.spaces.length > 0 && (() => {
                          const isAvailability = msg.spaces[0]?.bookingMode === 'availability';
                          const headerText = isAvailability ? 'Available Spaces' : 'Spaces You May Like';
                          const pag = msg.pagination;
                          const shownCount = msg.spaces.length;
                          const totalMatching = pag?.totalMatching ?? shownCount;
                          const remainingCount = pag?.remaining != null
                            ? pag.remaining
                            : Math.max(0, totalMatching - shownCount);
                          const resultsMapUrl = msg.filters
                            ? buildAgentSearchResultsUrl(msg.filters, msg.searchContext, { openMap: true })
                            : null;
                          return (
                          <div className="v2-space-cards">
                            <div className="v2-spaces-header">
                              <div className="v2-spaces-title">{headerText}</div>
                              {resultsMapUrl && (
                                <a
                                  href={resultsMapUrl}
                                  className="v2-view-map-link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View on map →
                                </a>
                              )}
                            </div>
                            {totalMatching > shownCount && (
                              <p className="v2-results-summary">
                                Showing {shownCount} of {totalMatching} spaces
                              </p>
                            )}
                            {msg.spaces.map((space, si) => {
                              const imgUrl = space.image && /^https?:\/\//i.test(space.image) 
                                ? space.image 
                                : space.image 
                                  ? `${IMAGES_BASE_URL}${space.image}` 
                                  : null;
                              const cardBookingUrl = resolveSpaceCardBookingUrl(space, () => buildBookingUrl(
                                space.id,
                                space.country || marketCode,
                                space.bookingUnit || 'hour',
                                space.bookingStartDate,
                                space.bookingEndDate,
                                space.eventTypeId,
                                space.bookingStartTime,
                                space.bookingEndTime,
                                msg.filters?.peopleCount,
                              ));
                              const pricingDisplay = space.matchedPrice
                                ? `${space.matchedPrice.currency} ${space.matchedPrice.amount.toLocaleString()} / ${space.matchedPrice.perUnit}`
                                : (space.pricing?.find((p) => p !== 'Contact for pricing') || null);
                              const locationText = [space.address, space.city].filter(Boolean).join(', ');
                              const hasTimeFilter = Boolean(msg.filters?.timeRange);
                              // When a time was requested but this space isn't free then, offer a
                              // "Book here" link (opens the booking page to pick another time) instead
                              // of a Book Now that pre-fills an unavailable slot.
                              const timeUnavailable = hasTimeFilter && space.requestedTimeAvailable === false;
                              const buttonLabel = timeUnavailable
                                ? 'Book here →'
                                : isAvailability ? 'Book Now →' : 'View Details →';
                              const displayName = space.organizationName || space.hostedBy || null;

                              // Phase 1 — available slots (only when API returned availability)
                              const avail = isAvailability ? space.availability : null;
                              const availableSlots = avail?.slots?.filter((s) => s.status) || [];

                              return (
                                <div key={space.id || `space-${si}`} className="v2-space-card">
                                  <div className="v2-space-card-image">
                                    {imgUrl ? (
                                      <img
                                        src={imgUrl}
                                        alt={space.name}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className="v2-space-placeholder" style={{ display: imgUrl ? 'none' : 'flex' }}>
                                      🏢
                                    </div>
                                  </div>
                                  <div className="v2-space-card-body">
                                    <div className="v2-space-name">{space.name}</div>
                                    {displayName && (
                                      <div className="v2-space-hosted">by {displayName}</div>
                                    )}
                                    {locationText && (
                                      <div className="v2-space-location">
                                        <span className="v2-location-icon">📍</span>
                                        <span>
                                          {locationText}
                                          {msg.filters?.showDistanceKm && space.distanceKm != null && (
                                            <span className="v2-distance"> · {space.distanceKm.toFixed(1)} km</span>
                                          )}
                                        </span>
                                      </div>
                                    )}
                                    {pricingDisplay && (
                                      <div className="v2-space-pricing">{pricingDisplay}</div>
                                    )}
                                    {space.capacity && (
                                      <div className="v2-space-capacity">Up to {space.capacity} people</div>
                                    )}

                                    {/* Phase 3 — amenity tags */}
                                    {space.amenities && space.amenities.length > 0 && (
                                      <div className="v2-amenity-tags">
                                        {space.amenities.slice(0, 4).map((a, ai) => (
                                          <span key={ai} className="v2-amenity-tag">{a.name}</span>
                                        ))}
                                        {space.amenities.length > 4 && (
                                          <span className="v2-amenity-tag v2-amenity-more">
                                            +{space.amenities.length - 4}
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Requested-time availability (renders in browse + availability mode) */}
                                    {hasTimeFilter && (space.requestedTimeAvailable != null || space.timeNote) && (
                                      <div className={`v2-time-availability${
                                        space.requestedTimeAvailable === false ? ' v2-time-availability--unavailable'
                                          : space.requestedTimeAvailable == null ? ' v2-time-availability--note' : ''
                                      }`}>
                                        {space.requestedTimeAvailable === true && (
                                          <div className="v2-avail-label">✓ Available for {msg.filters.timeRange}</div>
                                        )}
                                        {space.requestedTimeAvailable === false && space.timeNote && (
                                          <div className="v2-avail-label v2-avail-warning">⚠ {space.timeNote}</div>
                                        )}
                                        {space.requestedTimeAvailable == null && space.timeNote && (
                                          <div className="v2-avail-label v2-avail-note">🕐 {space.timeNote}</div>
                                        )}
                                      </div>
                                    )}

                                    {/* Phase 1 — inline availability */}
                                    {avail && (
                                      <div className="v2-availability">
                                        {availableSlots.length > 0 ? (
                                          <>
                                            <div className="v2-avail-label">✓ Available slots</div>
                                            {availableSlots.slice(0, 3).map((slot, slotI) => (
                                              <div key={slotI} className="v2-slot">
                                                {slot.startTime} – {slot.endTime}
                                              </div>
                                            ))}
                                            {availableSlots.length > 3 && (
                                              <div className="v2-slot-more">
                                                +{availableSlots.length - 3} more slots
                                              </div>
                                            )}
                                          </>
                                        ) : avail.availableSeats !== null && avail.availableSeats !== undefined ? (
                                          <div className="v2-avail-label">
                                            ✓ {avail.availableSeats} seat{avail.availableSeats !== 1 ? 's' : ''} available
                                          </div>
                                        ) : !hasTimeFilter ? (
                                          <div className="v2-avail-label">✓ Available</div>
                                        ) : null}
                                      </div>
                                    )}

                                    <button className="v2-book-btn" onClick={() => window.open(cardBookingUrl, '_blank')}>
                                      {buttonLabel}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            {remainingCount > 0 && (
                              <div className="v2-pagination">
                                <p className="v2-pagination-text">
                                  {buildMoreResultsHint(remainingCount)}
                                </p>
                              </div>
                            )}
                          </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  <span className="message-time">{formatTime(msg.timestamp)}</span>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            {showVoicePromo && (
              <VoiceModePromo onStart={enterVoiceMode} />
            )}
            </div>

            {/* Contact form */}
            {showContactForm && (
              <div className="chatbot-contact-form">
                <h4>{CONTACT_FORM_MESSAGES.TITLE}</h4>
                <div className="agent-contact-details">
                  <div className="agent-contact-message">{CONTACT_FORM_MESSAGES.AGENT_MESSAGE}</div>
                  <div className="agent-phone-number">
                    <a href={`tel:${CONTACT_FORM_MESSAGES.AGENT_PHONE}`} className="phone-link">
                      {CONTACT_FORM_MESSAGES.AGENT_PHONE}
                    </a>
                  </div>
                </div>
                <form onSubmit={handleContactFormSubmit}>
                  {[
                    { label: FORM_LABELS.NAME, type: INPUT_TYPES.TEXT, name: FORM_FIELD_NAMES.NAME, placeholder: FORM_PLACEHOLDERS.NAME, value: contactForm.name },
                    { label: FORM_LABELS.EMAIL, type: INPUT_TYPES.EMAIL, name: FORM_FIELD_NAMES.EMAIL, placeholder: FORM_PLACEHOLDERS.EMAIL, value: contactForm.email },
                    { label: FORM_LABELS.PHONE_NUMBER, type: INPUT_TYPES.TEL, name: FORM_FIELD_NAMES.PHONE_NUMBER, placeholder: FORM_PLACEHOLDERS.PHONE_NUMBER, value: contactForm.phoneNumber, optional: true },
                  ].map((field) => (
                    <div key={field.name} className="form-group">
                      <label className="form-label">
                        {field.label} {field.optional && <span className="optional-text">(Optional)</span>}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        placeholder={field.placeholder}
                        value={field.value}
                        onChange={handleContactFormChange}
                        className={contactFormErrors[field.name] ? 'error' : ''}
                      />
                      {contactFormErrors[field.name] && <div className="form-error">{contactFormErrors[field.name]}</div>}
                    </div>
                  ))}
                  <div className="form-group">
                    <label className="form-label">{FORM_LABELS.MESSAGE}</label>
                    <textarea
                      name={FORM_FIELD_NAMES.MESSAGE}
                      placeholder={FORM_PLACEHOLDERS.MESSAGE}
                      value={contactForm.message}
                      onChange={handleContactFormChange}
                      className={contactFormErrors.message ? 'error' : ''}
                    />
                    {contactFormErrors.message && <div className="form-error">{contactFormErrors.message}</div>}
                  </div>
                  <div className="form-actions">
                    <button type="submit" disabled={loading || !isFormReadyToSubmit(contactForm)}>
                      {loading ? LOADING_MESSAGES.SENDING_MESSAGE : FORM_BUTTONS.SEND_MESSAGE}
                    </button>
                    <button type="button" onClick={() => { setShowContactForm(false); resetForm(setContactForm); setContactFormErrors({}); }}>
                      {FORM_BUTTONS.CANCEL}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Phase 8 — offline banner */}
            {isOffline && (
              <div className="v2-offline-banner">
                You're offline — reconnect to keep chatting.
              </div>
            )}

            {/* Live voice bar (replaces the input row while talking) */}
            {!showContactForm && voiceModeActive && (
              <VoiceModeBar
                userContext={userContext}
                voice={REALTIME_VOICE}
                onClose={exitVoiceMode}
                onCaption={handleVoiceCaption}
                onToolResult={handleVoiceToolResult}
              />
            )}

            {/* Input */}
            {!showContactForm && !voiceModeActive && (
              <div className="chatbot-input">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isListening ? 'Listening...' : 'Ask me anything about workspaces...'}
                  disabled={loading}
                />
                <span
                  className="voice-input-tooltip"
                  title={speechSupported ? (isListening ? 'Stop voice input' : 'Start voice input') : 'Voice input is not supported in this browser.'}
                >
                  <button
                    type="button"
                    className={`voice-input-button${isListening ? ' listening' : ''}`}
                    onClick={handleVoiceToggle}
                    disabled={loading || !speechSupported}
                    aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
                    aria-pressed={isListening}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M12 14c1.66 0 3-1.34 3-3V6c0-1.66-1.34-3-3-3S9 4.34 9 6v5c0 1.66 1.34 3 3 3z" />
                      <path d="M17.3 11c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z" />
                    </svg>
                  </button>
                </span>
                <button onClick={handleSend} disabled={loading || isListening || !inputMessage.trim()}>
                  {loading ? '⏳' : (
                    <div className="input-send">
                      <img src={require('../../../assets/images/agent/send.svg')} alt="Send" width="23" height="23" />
                    </div>
                  )}
                </button>
                {speechError && <div className="voice-input-message">{speechError}</div>}
              </div>
            )}

            {/* Disclaimer + info */}
            <div className="chatbot-disclaimer">
              <div className="disclaimer-content">
                <span className="disclaimer-text">{DISCLAIMER_MESSAGES.TEXT}</span>
                <button className="info-button" onClick={() => setShowInfo(!showInfo)} title="More information">
                  <img src={require('../../../assets/images/agent/info-circle.svg')} alt="Info" width="16" height="16" />
                </button>
              </div>

              {showInfo && (
                <div className="info-modal">
                  <div className="info-content">
                    <div className="info-header">
                      <h4>{INFO_PANEL_MESSAGES.ABOUT_SPACEY}</h4>
                      <button className="close-icon" onClick={() => setShowInfo(false)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" /></svg>
                      </button>
                    </div>
                    <div className="info-body">
                      <p>{INFO_PANEL_MESSAGES.DESCRIPTION}</p>
                      <h5>{INFO_PANEL_MESSAGES.HELP_WITH_TITLE}</h5>
                      <ul>
                        {HELP_INFORMATION.CAN_HELP_WITH.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                      <h5>{INFO_PANEL_MESSAGES.CONTACT_AGENT_TITLE}</h5>
                      <ul>
                        {HELP_INFORMATION.CONTACT_AGENT.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                      <h5>{INFO_PANEL_MESSAGES.IMPORTANT_NOTES_TITLE}</h5>
                      <ul>
                        {HELP_INFORMATION.IMPORTANT_NOTES.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                      <button className="close-info" onClick={() => setShowInfo(false)}>{INFO_PANEL_MESSAGES.GOT_IT}</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  );
};

ChatbotPopupV2.displayName = 'ChatbotPopupV2';

export default ChatbotPopupV2;

// ─── Greeting builder ─────────────────────────────────────────────────────────
function buildGreeting(isLoggedIn, userData, marketName) {
  const region = marketName ? ` in ${marketName}` : '';
  if (!isLoggedIn) {
    return `Hi there,\n\nI am Spacey, your smart assistant on MillionSpaces${region}, ready to help you find the perfect space.\n\nJust tell me what you're looking for — workspace type, location, or budget — and I'll find the best options for you.`;
  }
  const name = userData?.userName?.split(' ')[0] || 'there';
  const count = userData?.bookingCount || 0;
  if (count > 0) {
    return `Hi ${name},\n\nYou've booked ${count} time${count > 1 ? 's' : ''} with MillionSpaces! Great to have you back.\n\nWhat are you looking to book today?`;
  }
  return `Hi ${name},\n\nWelcome to MillionSpaces${region}! I'm Spacey, your workspace assistant.\n\nTell me what kind of space you need and I'll find the best options for you.`;
}
